// Resolve a template item's relative due offset to an absolute task due
// date (Slice B §4.3). Pure + timezone-safe so the client preview
// (ApplyTemplateModal) and the server apply path compute identical dates —
// the server never trusts the client's resolved value.
//
// Rules (§4.3):
//   - relativeDueDays === null            -> null (task has no due date)
//   - plan has no promoStartDate          -> null (warned in the modal)
//   - else                                -> promoStart date + offset days
//
// Tasks store dueDate as a calendar date (YYYY-MM-DD, the plans isoDate zod
// shape). promoStartDate may be a legacy date-only string OR a datetime with
// the Sydney offset (Consolidation §1.1). We take the leading YYYY-MM-DD
// (the Sydney wall-clock calendar day — promo datetimes are authored in
// Sydney) and do the day arithmetic in UTC so a non-UTC server can't shift
// the date.
const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

export function resolveTemplateDueDate(
  promoStartDate: string | null,
  relativeDueDays: number | null,
): string | null {
  if (relativeDueDays === null) return null;
  if (!promoStartDate) return null;
  const m = DATE_PREFIX.exec(promoStartDate);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]); // 1-12
  const day = Number(m[3]);
  // Date.UTC + setUTCDate handles month/year rollover correctly, including
  // negative offsets (e.g. 28 days before launch).
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + relativeDueDays);
  const yyyy = String(base.getUTCFullYear()).padStart(4, '0');
  const mm = String(base.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(base.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Human-readable relative-due summary ("28 days before launch", "On launch
// day", "No due date"). Shared by the Settings item list/form and the
// apply-template preview — pure, in lib/ so neither module reaches across
// the other's boundary for it.
export function formatRelativeDue(days: number | null): string {
  if (days === null) return 'No due date';
  if (days === 0) return 'On launch day';
  return days < 0
    ? `${-days} day${days === -1 ? '' : 's'} before launch`
    : `${days} day${days === 1 ? '' : 's'} after launch`;
}
